/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */


import {animate, state, style, transition, trigger} from '@angular/animations';
import {Component, EventEmitter, Input, OnDestroy, Optional, Output, SkipSelf} from '@angular/core';

import {Expand} from '../../utils/expand/providers/expand';
import {LoadingListener} from '../../utils/loading/loading-listener';

import {AbstractTreeSelection} from './abstract-tree-selection';
import {clrTreeSelectionProviderFactory} from './providers/tree-selection.provider';
import {TreeSelectionService} from './providers/tree-selection.service';
export class TreeNode extends AbstractTreeSelection implements OnDestroy {
/**
 * @param {?} nodeExpand
 * @param {?} parent
 * @param {?} treeSelectionService
 */
constructor(public nodeExpand: Expand,
public parent: TreeNode,
public treeSelectionService: TreeSelectionService) {
        super(parent);
        if (this.parent) {
            this.parent.register(this);
        }
    }
private _children: TreeNode[] = [];
/**
 * @return {?}
 */
get children(): TreeNode[] {
        return this._children;
    }
/**
 * @param {?} node
 * @return {?}
 */
checkIfChildNodeRegistered(node: TreeNode): boolean {
        return (this.children.indexOf(node) > -1);
    }
/**
 * @param {?} node
 * @return {?}
 */
register(node: TreeNode): void {
        if (!this.checkIfChildNodeRegistered(node)) {
            this.children.push(node);
            if (this.selectable) {
                if (this.selected) {
                    node.parentChanged(this.selected);
                }
            }
        }
    }
/**
 * @param {?} node
 * @return {?}
 */
unregister(node: TreeNode): void {
        const /** @type {?} */ index = this.children.indexOf(node);
        if (index > -1) {
            this.children.splice(index, 1);
        }
    }
/**
 * @return {?}
 */
activateSelection(): void {
        if (this.treeSelectionService && !this.treeSelectionService.selectable) {
            this.treeSelectionService.selectable = true;
        }
    }
/**
 * @param {?} value
 * @return {?}
 */
public set nodeSelected(value: boolean) {
        // required for recursive trees to discard unset inputs.
        this.activateSelection();
        if (value === undefined || value === null) {
            return;
        }
        if (this.selected !== value) {
            this.selected = value;
        }
    }

     nodeSelectedChange: EventEmitter<boolean> = new EventEmitter<boolean>(true);
/**
 * @return {?}
 */
selectedChanged(): void {
        this.nodeSelectedChange.emit(this.selected);
    }
/**
 * @return {?}
 */
get selectable(): boolean {
        if (this.treeSelectionService) {
            return this.treeSelectionService.selectable;
        }
        return false;
    }
/**
 * @param {?} value
 * @return {?}
 */
set nodeIndeterminate(value: boolean) {
        this.indeterminate = value;
        this.activateSelection();
    }

     nodeIndeterminateChanged: EventEmitter<boolean> = new EventEmitter<boolean>(true);
/**
 * @return {?}
 */
indeterminateChanged(): void {
        this.nodeIndeterminateChanged.emit(this.indeterminate);
    }
/**
 * @return {?}
 */
toggleExpand(): void {
        this.nodeExpand.expanded = !this.nodeExpand.expanded;
    }
/**
 * @return {?}
 */
public get caretDirection(): string {
        return (this.nodeExpand.expanded) ? "down" : "right";
    }
/**
 * @return {?}
 */
get expanded(): boolean {
        return this.nodeExpand.expanded;
    }
/**
 * @param {?} value
 * @return {?}
 */
set expanded(value: boolean) {
        value = !!value;
        if (this.nodeExpand.expanded !== value) {
            this.nodeExpand.expanded = value;
        }
    }
/**
 * @return {?}
 */
get state(): string {
        return (this.expanded && !this.nodeExpand.loading) ? "expanded" : "collapsed";
    }
/**
 * @return {?}
 */
ngOnDestroy() {
        if (this.parent) {
            this.parent.unregister(this);
        }
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "clr-tree-node",
    template: `
      <div class="clr-tree-node-content-container">
          <button
              type="button"
              class="clr-treenode-caret"
              (click)="toggleExpand()"
              *ngIf="nodeExpand.expandable && !nodeExpand.loading">
              <clr-icon
                  class="clr-treenode-caret-icon"
                  shape="caret"
                  [attr.dir]="caretDirection"></clr-icon>
          </button>
          <div class="clr-treenode-spinner-container" *ngIf="nodeExpand.expandable && nodeExpand.loading">
              <span class="clr-treenode-spinner spinner">
                  Loading...
              </span>
          </div>
          <clr-checkbox
              class="clr-treenode-checkbox"
              *ngIf="selectable"
              [(ngModel)]="selected"
              [(clrIndeterminate)]="indeterminate"></clr-checkbox>
          <div class="clr-treenode-content">
              <ng-content></ng-content>
          </div>
      </div>
      <!-- FIXME: remove this string concatenation when boolean states are supported -->
      <div
          class="clr-treenode-children"
          [@childNodesState]="state">
          <ng-content select="clr-tree-node"></ng-content>
          <ng-content select="[clrIfExpanded]"></ng-content>
      </div>
    `,
    providers: [
        Expand, {provide: LoadingListener, useExisting: Expand}, {
            provide: TreeSelectionService,
            useFactory: clrTreeSelectionProviderFactory,
            deps: [[new Optional(), new SkipSelf(), TreeSelectionService]]
        }
    ],
    animations: [trigger("childNodesState",
                         [
                             state("expanded", style({"height": "*", "overflow-y": "hidden"})),
                             state("collapsed", style({"height": 0, "overflow-y": "hidden"})),
                             transition("expanded <=> collapsed", animate("0.2s ease-in-out"))
                         ])],
    host: {"class": ".clr-tree-node"}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Expand, },
{type: TreeNode, decorators: [{ type: Optional }, { type: SkipSelf }, ]},
{type: TreeSelectionService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'nodeSelected': [{ type: Input, args: ["clrSelected", ] },],
'nodeSelectedChange': [{ type: Output, args: ["clrSelectedChange", ] },],
'nodeIndeterminate': [{ type: Input, args: ["clrIndeterminate", ] },],
'nodeIndeterminateChanged': [{ type: Output, args: ["clrIndeterminateChange", ] },],
};
}

function TreeNode_tsickle_Closure_declarations() {
/** @type {?} */
TreeNode.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TreeNode.ctorParameters;
/** @type {?} */
TreeNode.propDecorators;
/** @type {?} */
TreeNode.prototype._children;
/** @type {?} */
TreeNode.prototype.nodeSelectedChange;
/** @type {?} */
TreeNode.prototype.nodeIndeterminateChanged;
/** @type {?} */
TreeNode.prototype.nodeExpand;
/** @type {?} */
TreeNode.prototype.parent;
/** @type {?} */
TreeNode.prototype.treeSelectionService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
